/**
 * Branch charges (accounts): every customer of one branch over a period with their traffic fines,
 * Salik, Darb and parking - each line on Ejar or not, per-customer totals, what is still to charge.
 * Built by the engine from local data (/api/reports/branch-charges): a month answers in seconds.
 * Each line has ONE state from the engine (on Ejar / Unlimited Salik / not on Ejar / paid by customer at RTA):
 * the cells, the cards, the lines, the copied table and the Excel all say the same (owner, 28 Sep 2026).
 */
import { formatPlateNumber } from '../../utils/plateFormatter';
import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import EmptyState from '../../ui/EmptyState';
import { Input, Switch } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellId, CellMuted, CellText, toneClass } from '../../ui/DataTable/cells';
import LoadingSpinner from '../../components/LoadingSpinner';
import { finesService, errorMessage } from '../../services/finesService';
import { money, htmlTable, copyHtml, Notice } from '../OperationsPortal/components/collectionsShared';
import { PRESETS, periodOf, matchRows, ymd, nextInvoiceNo, invygoInvoices, invygoSheetRows, LINE_STATES, lineState, kindParts, kindText, kindCardText, coverageNotices, emptyText } from './branchCharges.logic';
import { fitContractDateColumns } from '../../utils/excelDates';
import Dialog from '../../ui/Dialog/Dialog';
import { contractsService } from '../../services/firestore';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { usePermissions } from '../../hooks/usePermissions';
import { invygoIdsFor } from '../../services/invygoIds';
import { suggestNextInvoiceNumber, issueInvoiceNumbers, parseInvoiceNumber, invoiceCounterOn } from '../../services/invoiceNumbers';
import * as XLSX from 'xlsx';

const BRANCHES = [['T1', 'T1'], ['T2', 'T2'], ['BB', 'B.B'], ['INV', 'INV'], ['all', 'All']];
const KINDS = [['fine', 'Fines'], ['toll', 'Salik'], ['darb', 'Darb'], ['parking', 'Parking']];
const KIND_NAME = Object.fromEntries(KINDS);
const PREF = 'branchCharges.prefs';
// a card picks the customers shown: those with that kind, or those with something still to charge
const PICK = {
    fine: (r) => r.kinds.fine.n > 0, toll: (r) => r.kinds.toll.n > 0, darb: (r) => r.kinds.darb.n > 0,
    parking: (r) => r.kinds.parking.n > 0, owed: (r) => r.notOnEjar > 0.005,
};

const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREF) || '{}'); } catch { return {}; } };

export function Segmented({ value, onChange, options, label }) {
    return (
        <div className="ds-tabs--segmented" role="group" aria-label={label}>
            {options.map(([id, text]) => (
                <button key={id} type="button" aria-pressed={value === id} onClick={() => onChange(id)}
                    className={`px-3 py-1.5 text-sm font-medium ${value === id ? 'bg-purple-700 text-white' : 'text-gray-800 hover:bg-purple-50'}`}>
                    {text}
                </button>
            ))}
        </div>
    );
}

/** A compact summary card; clickable ones filter the table (click again: everyone). */
export function Card({ label, value, sub, tone, active, onClick }) {
    const body = (
        <>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
            <span className={`block text-lg font-bold tabular-nums leading-tight ${tone || 'text-gray-900'}`}>{value}</span>
            {sub && <span className="block text-[11px] text-gray-500 truncate" title={typeof sub === 'string' ? sub : undefined}>{sub}</span>}
        </>
    );
    const base = 'text-center rounded-lg border-2 bg-white px-3 py-2 min-w-[132px] flex-1 transition-colors';
    if (!onClick) return <div className={`${base} border-purple-200`}>{body}</div>;
    return (
        <button type="button" onClick={onClick} aria-pressed={!!active} title={active ? 'Show everyone' : `Show only: ${label}`}
            className={`${base} ${active ? 'border-purple-700 bg-purple-50' : 'border-purple-200 hover:border-amber-400'}`}>
            {body}
        </button>
    );
}

/** The next invoice number to suggest: the one after the last used here (per kind), else ALWFQ-1. */
const INVOICE_KEY = (kind) => `invygoInvoice.last.${kind}`;
const suggestInvoice = (kind) => {
    try { const last = localStorage.getItem(INVOICE_KEY(kind)); return last ? nextInvoiceNo(last) : 'ALWFQ-1'; } catch { return 'ALWFQ-1'; }
};

/**
 * Invygo invoices (was the Matcher's Salik / Parking summaries): one invoice per contract of the report,
 * numbered on from the first number typed, with Invygo's own booking id (invygoIds) and the model from the contracts.
 * Numbering goes through the one invoice-number service (DUP-23). Today (counter off) it is as before: the
 * first number is suggested from the last one used in this browser (per kind). With the counter on, the next
 * free ALWFQ- number is filled in and the list's numbers are issued the first time it is saved or copied.
 */
function InvygoInvoicesDialog({ open, onClose, rows, from, to }) {
    const [kind, setKind] = useState('salik');
    const [first, setFirst] = useState(() => (invoiceCounterOn() ? '' : suggestInvoice('salik')));
    const [date, setDate] = useState(() => ymd(new Date()));
    const [store, setStore] = useState(null);
    const [msg, setMsg] = useState('');
    const [issued, setIssued] = useState('');   // counter on: the numbers already issued for this list (kind|first|count)

    // the contracts store (USE_CONTRACTS_V2): only the contracts of this report, by number (open or closed) - read
    // again for another report; Firestore: every contract, once
    const reportKey = [...new Set((rows || []).map((r) => String(r.contract || '').trim().toUpperCase()).filter(Boolean))].join(',');
    const contractsKey = USE_CONTRACTS_V2 ? reportKey : '';
    const storeKey = useRef(null);
    // Invygo's own booking id of the report's contracts (Ejar's booking -> broker reference; the old sheet's history
    // for old contracts). A failed read is said on the dialog - never an empty column that reads as "no booking".
    // `idsFor` = the report the ids were read for (a failed read leaves it unset, so opening the list again retries)
    const [invygoIds, setInvygoIds] = useState(null);
    const [idsError, setIdsError] = useState('');
    const idsFor = useRef(null);
    const idsAsked = useRef(null);
    useEffect(() => {
        if (!open || idsFor.current === reportKey) return;
        idsAsked.current = reportKey;
        setInvygoIds(null);
        invygoIdsFor(reportKey ? reportKey.split(',') : [])
            .then((found) => {
                if (idsAsked.current !== reportKey) return;
                idsFor.current = reportKey;
                setInvygoIds(found);
                setIdsError('');
            })
            .catch((err) => {
                if (idsAsked.current !== reportKey) return;
                setInvygoIds(new Map());
                setIdsError(errorMessage(err));
            });
    }, [open, reportKey]);
    useEffect(() => {
        if (!open || (store && storeKey.current === contractsKey)) return;
        storeKey.current = contractsKey;
        const read = USE_CONTRACTS_V2
            ? contractsService.getContractRowsByIds(contractsKey ? contractsKey.split(',') : [])
            : contractsService.getAllContracts();
        read
            .then((list) => setStore(new Map((list || []).map((c) => [String(c['Contract No.'] || '').toUpperCase(), c]))))
            .catch(() => setStore(new Map()));
    }, [open, store, contractsKey]);
    useEffect(() => {
        if (!open || !invoiceCounterOn()) return undefined;
        let live = true;
        suggestNextInvoiceNumber().then((next) => { if (live) setFirst((f) => f || next || ''); });
        return () => { live = false; };
    }, [open]);
    const pickKind = (k) => { setKind(k); if (!invoiceCounterOn()) setFirst(suggestInvoice(k)); setMsg(''); };

    const invoices = useMemo(() => invygoInvoices(rows, store, { kind, first, date, invygoIds }), [rows, store, kind, first, date, invygoIds]);
    const label = kind === 'parking' ? 'Parking' : 'Salik';
    const listKey = `${kind}|${first}|${invoices.length}`;
    const remember = () => { try { if (invoices.length) localStorage.setItem(INVOICE_KEY(kind), invoices[invoices.length - 1].invoice); } catch { /* storage blocked */ } };
    /** Counter on: issues the list's numbers once; false (and why) when they cannot be. Counter off: as before. */
    const issue = async () => {
        if (!invoiceCounterOn()) {
            remember();
            return true;
        }
        if (issued === listKey) return true;
        if (!parseInvoiceNumber(first)) {
            setMsg('! Type the first invoice number (ALWFQ-n).');
            return false;
        }
        try {
            await issueInvoiceNumbers(invoices.length, { typed: first });
            setIssued(listKey);
            return true;
        } catch (err) {
            setMsg('! ' + (err?.message || 'The invoice numbers could not be issued.'));
            return false;
        }
    };
    const COLS = [['invoice', 'Invoice'], ['customer', 'Customer'], ['booking', 'Booking Number'], ['contract', 'Contract No.'], ['model', 'Model'],
        ['plate', 'Plate No.'], ['start', 'Date'], ['end', 'End Date'], ['month', 'Month'], ['trips', kind === 'parking' ? 'Parking' : 'Salik Trips'],
        ['total', 'Total Price'], ['date', 'Invoice Date']];

    const excel = async () => {
        if (!(await issue())) return;
        // the contract's Date / End Date as real Excel dates (they sort and filter by date)
        const sheet = fitContractDateColumns(XLSX.utils.json_to_sheet(invygoSheetRows(invoices, COLS)));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, `Invygo ${label}`);
        XLSX.writeFile(book, `Invygo_${label}_Invoices_${from}_${to}.xlsx`);
        setMsg(`Excel saved - next ${label} invoice will start at ${nextInvoiceNo(invoices[invoices.length - 1].invoice)}.`);
    };
    const copy = async () => {
        if (!(await issue())) return;
        const html = htmlTable(COLS.map(([k, h]) => ({ h, get: (x) => (k === 'total' ? money(x[k]) : x[k]), bold: k === 'invoice' })), invoices,
            { label: 'TOTAL', cells: { 1: `${invoices.length} invoices`, 9: invoices.reduce((a, x) => a + x.trips, 0), 10: money(invoices.reduce((a, x) => a + x.total, 0)) } });
        const plain = invoices.map((x) => `${x.invoice} | ${x.contract} | ${x.customer} | ${x.trips} | ${money(x.total)}`).join('\n');
        setMsg(await copyHtml(html, plain, `${invoices.length} invoices copied - paste them into the email.`));
    };

    return (
        <Dialog open={open} onClose={onClose} size="xl" className="ds-dialog--brand" title={`Invygo invoices · ${from}${to !== from ? ` → ${to}` : ''}`}
            description="One invoice per contract: Salik + Darb trips, or parking. Type the first number - the rest follow on.">
            <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <Segmented value={kind} onChange={pickKind} options={[['salik', 'Salik + Darb'], ['parking', 'Parking']]} label="Invoice kind" />
                    <span className="inline-flex items-center gap-2 text-sm">First invoice <Input size="sm" value={first} onChange={(e) => setFirst(e.target.value)} style={{ width: 140 }} /></span>
                    <span className="inline-flex items-center gap-2 text-sm">Invoice date <Input size="sm" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></span>
                    <div className="flex-1" />
                    <Button size="sm" onClick={copy} disabled={!invoices.length} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>Copy for email</Button>
                    <Button size="sm" variant="primary" onClick={excel} disabled={!invoices.length} leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>Excel</Button>
                </div>
                {msg && <Notice tone={msg.startsWith('! ') ? 'danger' : 'success'}>{msg.replace(/^! /, '')}</Notice>}
                {idsError && <Notice tone="danger">{`Invygo booking numbers could not be read (${idsError}) - the Booking Number column is empty. Close and open this list again to retry.`}</Notice>}
                {!store || !invygoIds ? <LoadingSpinner message="Reading the contracts (booking numbers)…" /> : (
                    <TableContainer maxHeight="55vh" className="overflow-auto">
                        <Table sticky className="w-full">
                            <TableHead><TableRow>{COLS.map(([k, h]) => <TableHeader key={k} className="whitespace-nowrap">{h}</TableHeader>)}</TableRow></TableHead>
                            <TableBody>
                                {invoices.map((x) => (
                                    <TableRow key={x.contract}>
                                        {COLS.map(([k]) => (
                                            <TableCell key={k} className={k === 'total' ? 'ds-cell-num font-semibold' : k === 'invoice' || k === 'contract' ? 'font-mono whitespace-nowrap' : ''}>
                                                {k === 'total' ? money(x[k]) : (x[k] === '' ? '—' : x[k])}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                                {!invoices.length && <TableRow><TableCell colSpan={COLS.length}><EmptyState icon={ICONS.wallet} title={`No ${label} in this period for Invygo.`} /></TableCell></TableRow>}
                                {invoices.length > 0 && (
                                    <TableRow className="is-total">
                                        <TableCell colSpan={9}>TOTAL · {invoices.length} invoice(s) · {invoices[0].invoice} → {invoices[invoices.length - 1].invoice}</TableCell>
                                        <TableCell className="ds-cell-num">{invoices.reduce((a, x) => a + x.trips, 0)}</TableCell>
                                        <TableCell className="ds-cell-num">{money(invoices.reduce((a, x) => a + x.total, 0))}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>
        </Dialog>
    );
}

/**
 * One kind's cell: the amount charged, how many, and what is true of them - "all on Ejar" only when every line is
 * really on Ejar. Each state in its one colour (LINE_STATES - the same as its line badge below).
 */
function KindCell({ k }) {
    if (!k || !k.n) return <span className="text-gray-400">—</span>;
    return (
        <span className="inline-flex flex-col items-center leading-tight">
            <span className="tabular-nums">{money(k.charge)}</span>
            <span className="text-[11px] text-gray-500">
                {k.n}
                {kindParts(k).map((p) => <span key={p.key} className={toneClass(p.tone)}> · {p.text}</span>)}
            </span>
        </span>
    );
}

/** The table takes the rest of the window (never less than 360 px). */
export function useFillHeight(dep) {
    const ref = useRef(null);
    const [h, setH] = useState(480);
    useLayoutEffect(() => {
        const measure = () => {
            if (!ref.current) return;
            const top = ref.current.getBoundingClientRect().top + window.scrollY;
            setH(Math.max(360, window.innerHeight - top - 40));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [dep]);
    return [ref, h];
}

export default function BranchCharges() {
    // the Excel / e-mail copy and the Invygo invoices (they give invoice numbers): each its own permission (permissions
    // phase 4, 4 Oct 2026)
    const { hasPermission } = usePermissions();
    const canExport = hasPermission('export_branch_charges');
    const canInvoice = hasPermission('branch_charges_invoices');
    const saved = loadPrefs();
    const [branch, setBranch] = useState(saved.branch || 'T1');
    const [preset, setPreset] = useState(saved.preset || 'yesterday');
    const [range, setRange] = useState(() => (saved.preset === 'custom' && saved.from ? { from: saved.from, to: saved.to } : periodOf(saved.preset || 'yesterday')));
    const [notOnEjar, setNotOnEjar] = useState(!!saved.notOnEjar);
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState('');
    const [err, setErr] = useState('');
    const [note, setNote] = useState('');
    const [search, setSearch] = useState('');
    const [pick, setPick] = useState('');           // '' | fine | toll | darb | parking | owed
    const [open, setOpen] = useState({});          // contract -> lines shown
    const [invoicing, setInvoicing] = useState(false);

    useEffect(() => {
        try { localStorage.setItem(PREF, JSON.stringify({ branch, preset, notOnEjar, ...(preset === 'custom' ? range : {}) })); } catch { /* storage blocked */ }
    }, [branch, preset, notOnEjar, range]);

    const pickPreset = (id) => {
        setPreset(id);
        if (id !== 'custom') setRange(periodOf(id));
    };

    const loadSeq = useRef(0);   // only the latest answer lands: a fast branch switch never shows the last branch's rows
    const load = async () => {
        if (!range.from || !range.to) return;
        const seq = ++loadSeq.current;
        setBusy('Building the report…'); setErr(''); setNote('');
        try {
            const got = await finesService.branchCharges({ branch, from: range.from, to: range.to, notOnEjar });
            if (seq === loadSeq.current) { setReport(got); setOpen({}); }
        } catch (e) { if (seq === loadSeq.current) { setErr(errorMessage(e)); setReport(null); } }
        finally { if (seq === loadSeq.current) setBusy(''); }
    };
    // the report follows the choices (a custom period once both days are set)
    useEffect(() => { load(); }, [branch, range.from, range.to, notOnEjar]);   // eslint-disable-line react-hooks/exhaustive-deps

    const rows = useMemo(() => matchRows(report?.rows || [], search).filter(PICK[pick] || (() => true)), [report, search, pick]);
    const s = report?.summary;
    const byKind = s?.byKind || {};
    const branchText = BRANCHES.find(([id]) => id === branch)?.[1] || branch;
    const choose = (id) => setPick((p) => (p === id ? '' : id));
    const [tableRef, tableH] = useFillHeight(`${!!s}-${err}-${note}-${Object.keys(report?.coverageMissing || {}).length}-${preset}`);

    const download = async () => {
        setBusy('Preparing the Excel file…');
        try { await finesService.branchChargesXlsx({ branch, from: range.from, to: range.to, notOnEjar }); }
        catch (e) { setErr(errorMessage(e)); }
        finally { setBusy(''); }
    };

    const copy = async () => {
        if (!rows.length) return;
        const cols = [
            { h: '#', get: (r, i) => i + 1 },
            { h: 'Contract', get: (r) => r.contract, bold: true },
            { h: 'Customer', get: (r) => r.customer },
            { h: 'Branch', get: (r) => r.short },
            // the same words as the cell: "15.15 (3 · 1 not on Ejar · 2 on Ejar)"
            ...KINDS.map(([k, label]) => ({ h: `${label} (AED)`, get: (r) => (r.kinds[k].n ? `${money(r.kinds[k].charge)} (${kindText(r.kinds[k])})` : '—') })),
            { h: 'Total (AED)', get: (r) => money(r.total), bold: true },
            { h: 'Not on Ejar (AED)', get: (r) => money(r.notOnEjar), red: true },
        ];
        const sum = (f) => money(rows.reduce((a, r) => a + f(r), 0));
        const html = `<p style="font-family:Arial,sans-serif;font-size:13px;"><b>Branch charges · ${branchText} · ${range.from}${range.to !== range.from ? ` to ${range.to}` : ''}</b></p>`
            + htmlTable(cols, rows, { label: 'TOTAL', cells: { 1: `${rows.length} customers`, 4: sum((r) => r.kinds.fine.charge), 5: sum((r) => r.kinds.toll.charge), 6: sum((r) => r.kinds.darb.charge), 7: sum((r) => r.kinds.parking.charge), 8: sum((r) => r.total), 9: sum((r) => r.notOnEjar) } });
        const plain = rows.map((r, i) => `${i + 1}. ${r.contract} | ${r.customer} | total ${money(r.total)} | not on Ejar ${money(r.notOnEjar)}`).join('\n');
        setNote(await copyHtml(html, plain, `Table copied (${rows.length} customers) - paste it into the email.`));
    };

    return (
        <Layout title="Branch charges" subtitle="Fines, Salik, Darb and parking of every customer of a branch - on Ejar or still to charge">
            <div className="flex flex-col gap-3">
                {/* choices: branch, period, what to show - one line */}
                <div className="flex flex-wrap items-center gap-3">
                    <Segmented value={branch} onChange={setBranch} options={BRANCHES} label="Branch" />
                    <Segmented value={preset} onChange={pickPreset} options={PRESETS.map((p) => [p.id, p.label])} label="Period" />
                    {preset === 'custom' && (
                        <span className="inline-flex items-center gap-2">
                            <Input type="date" size="sm" aria-label="From" value={range.from} max={range.to || undefined} onChange={(e) => setRange({ ...range, from: e.target.value })} />
                            <span className="text-gray-500">→</span>
                            <Input type="date" size="sm" aria-label="To" value={range.to} min={range.from || undefined} onChange={(e) => setRange({ ...range, to: e.target.value })} />
                        </span>
                    )}
                    <Switch checked={notOnEjar} onChange={setNotOnEjar} label="Not on Ejar only" />
                </div>

                {/* what is shown, the search and the actions - one line */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-700 whitespace-nowrap">
                        <b>{branchText}</b> · {range.from}{range.to !== range.from ? ` → ${range.to}` : ''}{report?.builtAt ? ` · built ${report.builtAt.slice(11, 16)}` : ''}
                    </span>
                    {busy && <span className="inline-flex items-center gap-1.5 text-sm text-gray-600" role="status"><LoadingSpinner size="sm" />{busy}</span>}
                    <div className="flex-1 min-w-[220px] max-w-[420px] ml-auto">
                        <div className="ds-search" style={{ flex: 'none', width: '100%' }}>
                            <Icon glyph={ICONS.search} size="sm" />
                            <input type="search" aria-label="Search customers" placeholder="Search a contract, customer or plate…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                    </div>
                    {canInvoice && branch === 'INV' && (
                        <Button size="sm" onClick={() => setInvoicing(true)} disabled={!!busy || !report?.rows?.length} leadingIcon={<Icon glyph={ICONS.invoices} size="sm" />}>Invygo invoices</Button>
                    )}
                    <Button size="sm" onClick={load} disabled={!!busy} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>Refresh</Button>
                    {canExport && <Button size="sm" onClick={copy} disabled={!!busy || !rows.length} leadingIcon={<Icon glyph={ICONS.mail} size="sm" />}>Copy for email</Button>}
                    {canExport && <Button size="sm" variant="primary" onClick={download} disabled={!!busy || !report?.rows?.length} leadingIcon={<Icon glyph={ICONS.download} size="sm" />}>Excel</Button>}
                </div>

                {err && <Notice tone="danger">{err}</Notice>}
                {note && <Notice tone="success">{note}</Notice>}
                {coverageNotices(report).map((text) => <Notice key={text} tone="warning">{text}</Notice>)}

                {/* the totals - one line of compact cards; a card filters the table */}
                {s && (
                    <div className="flex flex-wrap gap-2">
                        <Card label="Customers" value={s.withCharges} sub={`of ${s.contracts} contract(s)`} active={!pick} onClick={() => setPick('')} />
                        {KINDS.map(([k, label]) => (
                            <Card key={k} label={label} value={money(byKind[k]?.charge)} active={pick === k} onClick={() => choose(k)}
                                sub={kindCardText(byKind[k], money)} />
                        ))}
                        <Card label="Total (AED)" value={money(s.total)} />
                        <Card label="Not on Ejar (AED)" value={money(s.notOnEjar)} tone={s.notOnEjar > 0.005 ? 'text-red-700' : 'text-green-700'}
                            sub={`${s.customersToCharge} customer(s) to charge`} active={pick === 'owed'} onClick={() => choose('owed')} />
                    </div>
                )}

                <div ref={tableRef}>
                    <TableContainer maxHeight={`${tableH}px`} className="overflow-auto">
                        <Table sticky className="w-full">
                            <TableHead>
                                <TableRow>
                                    {['#', 'Contract', 'Customer', 'Branch', 'Status', ...KINDS.map(([, l]) => l), 'Total', 'Not on Ejar', ''].map((h, i) => (
                                        <TableHeader key={i} className="whitespace-nowrap">{h || <span className="sr-only">Lines</span>}</TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <Fragment key={r.contract}>
                                        <TableRow className="cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [r.contract]: !o[r.contract] }))}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell className="whitespace-nowrap"><CellId>{r.contract}</CellId><CellMuted>{r.plates.map(formatPlateNumber).join(', ')}</CellMuted></TableCell>
                                            <TableCell><CellText>{r.customer}</CellText></TableCell>
                                            <TableCell><Badge size="sm" variant="neutral">{r.short}</Badge></TableCell>
                                            <TableCell><Badge size="sm" variant={r.isOpen ? 'info' : 'neutral'}>{r.isOpen ? 'Open' : 'Closed'}</Badge></TableCell>
                                            {KINDS.map(([k]) => <TableCell key={k}><KindCell k={r.kinds[k]} /></TableCell>)}
                                            <TableCell className="ds-cell-num font-semibold">{money(r.total)}</TableCell>
                                            <TableCell className={`ds-cell-num font-semibold ${toneClass(r.notOnEjar > 0.005 ? 'danger' : 'success')}`}>{money(r.notOnEjar)}</TableCell>
                                            <TableCell><Icon glyph={open[r.contract] ? ICONS.chevronUp : ICONS.chevronDown} size="sm" /></TableCell>
                                        </TableRow>
                                        {open[r.contract] && (
                                            <TableRow>
                                                <TableCell colSpan={12} className="bg-purple-50/40">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-gray-600">{['Date', 'Kind', 'Plate', 'Where / offense', 'Amount', 'Charged', 'Owed', 'Ejar', 'Ref'].map((h) => <th key={h} className="py-1 px-2 text-center font-semibold">{h}</th>)}</tr>
                                                        </thead>
                                                        <tbody>
                                                            {r.lines.map((x, j) => (
                                                                <tr key={j} className="border-t border-purple-100">
                                                                    <td className="py-1 px-2 text-center tabular-nums whitespace-nowrap">{x.when}</td>
                                                                    <td className="py-1 px-2 text-center">{KIND_NAME[x.kind]}</td>
                                                                    <td className="py-1 px-2 text-center font-mono">{formatPlateNumber(x.plate)}</td>
                                                                    <td className="py-1 px-2 text-center">{x.place || '—'}</td>
                                                                    <td className="py-1 px-2 text-center tabular-nums">{money(x.amount)}</td>
                                                                    <td className="py-1 px-2 text-center tabular-nums font-semibold">{money(x.charge)}</td>
                                                                    {/* still to charge: an amount-mismatch fine owes only its shortfall */}
                                                                    <td className={`py-1 px-2 text-center tabular-nums font-semibold ${x.owed > 0.005 ? 'text-red-700' : 'text-gray-500'}`}>{money(x.owed)}</td>
                                                                    <td className="py-1 px-2 text-center">
                                                                        {/* the line's one state: "On Ejar" only when Ejar really holds it; Unlimited Salik is
                                                                            covered (not charged, not on Ejar); a fine paid at RTA is never billed (MON-08) */}
                                                                        <Badge size="sm" variant={LINE_STATES[lineState(x)].variant}>{LINE_STATES[lineState(x)].label}</Badge>
                                                                        {x.note ? <span className="block text-[10px] text-gray-500">{x.note}</span> : null}
                                                                    </td>
                                                                    <td className="py-1 px-2 text-center font-mono text-gray-500">{x.ref}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                ))}
                                {report && !rows.length && (
                                    <TableRow><TableCell colSpan={12}>
                                        <EmptyState icon={ICONS.wallet} title={emptyText({ search, pick, notOnEjar })} />
                                    </TableCell></TableRow>
                                )}
                                {!report && busy && (
                                    <TableRow><TableCell colSpan={12}><div className="py-6"><LoadingSpinner message={busy} /></div></TableCell></TableRow>
                                )}
                                {rows.length > 0 && (
                                    <TableRow className="is-total">
                                        <TableCell colSpan={5}>TOTAL · {rows.length} customer(s){search || pick ? ' (filtered)' : ''}</TableCell>
                                        {KINDS.map(([k]) => <TableCell key={k} className="ds-cell-num">{money(rows.reduce((a, r) => a + r.kinds[k].charge, 0))}</TableCell>)}
                                        <TableCell className="ds-cell-num">{money(rows.reduce((a, r) => a + r.total, 0))}</TableCell>
                                        <TableCell className={`ds-cell-num ${toneClass('danger')}`}>{money(rows.reduce((a, r) => a + r.notOnEjar, 0))}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div className="text-xs text-gray-600 mt-2">
                        Click a customer for every line · Salik / Darb: gate + 1.05 AED per trip, none on an Unlimited Salik contract (as its Ejar calculation says) · parking: portal amount · fines: fine + service fee + VAT · click a contract number for Ejar / The Engine / Customer profile.
                    </div>
                </div>
            </div>
            {branch === 'INV' && (
                <InvygoInvoicesDialog open={invoicing} onClose={() => setInvoicing(false)} rows={report?.rows || []} from={range.from} to={range.to} />
            )}
        </Layout>
    );
}
